import { DeviceRegistry } from './registry';
import type { DeviceCategory, DeviceDefinition, DeviceType } from './types';
import type { PropertyDefinition, PropertyValues, PropertyValue } from '@/core/types';
import type { TranslationKey } from '@/i18n';

/**
 * Phase 1 catalog: every device kind of the roadmap is registered as `'planned'` metadata only,
 * so the Components panel is already driven by the real registry. Phases 8–13 replace these
 * entries with full definitions (behavior, renderer, terminals, property schema).
 */
const PLANNED: readonly (readonly [DeviceType, DeviceCategory])[] = [
  ['button', 'input'],
  ['switch', 'input'],
  ['emergency-stop', 'input'],
  ['sensor', 'input'],
  ['jam-sensor', 'input'],
  ['level-sensor', 'input'],
  ['lamp', 'output'],
  ['motor', 'output'],
  ['buzzer', 'output'],
  ['fan', 'output'],
  ['conveyor', 'mechanical'],
  ['cylinder', 'mechanical'],
  ['valve', 'mechanical'],
  ['tank', 'process'],
  ['pump', 'process'],
  ['mixer', 'process'],
  ['product', 'process'],
  ['pid-controller', 'process'],
  ['modbus-server', 'network'],
  ['modbus-client', 'network'],
];

const GRID_UNIT = 40;

function plannedDefinition(type: DeviceType, category: DeviceCategory): DeviceDefinition {
  const implementedTypes: DeviceType[] = ['button', 'emergency-stop', 'switch', 'lamp', 'buzzer', 'motor', 'fan', 'sensor', 'jam-sensor', 'conveyor', 'tank', 'pump', 'mixer', 'valve', 'level-sensor', 'pid-controller', 'modbus-server', 'modbus-client'];
  
  const schema: PropertyDefinition[] = [{ key: 'address', labelKey: 'properties.general', kind: 'text' }];
  const defaultProps: Record<string, PropertyValue> = { address: '' };

  const colorProperty = {
    key: 'color',
    labelKey: 'properties.color' as TranslationKey,
    kind: 'select' as const,
    options: [
      { value: '#1e40af', labelKey: 'color.industrialBlue' as TranslationKey },
      { value: 'var(--color-primary, #3b82f6)', labelKey: 'color.blue' as TranslationKey },
      { value: 'var(--color-success, #22aa22)', labelKey: 'color.green' as TranslationKey },
      { value: 'var(--color-danger, #ef4444)', labelKey: 'color.red' as TranslationKey },
      { value: 'var(--color-warning, #eab308)', labelKey: 'color.yellow' as TranslationKey },
      { value: '#0891b2', labelKey: 'color.cyan' as TranslationKey },
      { value: '#4b5563', labelKey: 'color.gray' as TranslationKey },
      { value: '#ea580c', labelKey: 'color.orange' as TranslationKey },
      { value: '#ffffff', labelKey: 'color.white' as TranslationKey },
    ]
  };

  if (type === 'conveyor') {
    schema.push(
      colorProperty,
      { key: 'motorId', labelKey: 'properties.motorId', kind: 'text' },
      { key: 'length', labelKey: 'properties.length', kind: 'text' },
      { key: 'width', labelKey: 'properties.width', kind: 'text' },
      { key: 'speed', labelKey: 'properties.speed', kind: 'text' },
      {
        key: 'direction',
        labelKey: 'properties.direction' as TranslationKey,
        kind: 'select',
        options: [
          { value: 'RIGHT', labelKey: 'direction.right' as TranslationKey },
          { value: 'LEFT', labelKey: 'direction.left' as TranslationKey },
        ],
      },
      {
        key: 'endAction',
        labelKey: 'properties.endAction' as TranslationKey,
        kind: 'select',
        options: [
          { value: 'DROP_OFF', labelKey: 'endAction.dropOff' as TranslationKey },
          { value: 'STOPPER', labelKey: 'endAction.stopper' as TranslationKey },
        ],
      },
      { key: 'minimumProductSpacing', labelKey: 'properties.minSpacing', kind: 'text' },
      { key: 'jamThresholdMs', labelKey: 'properties.jamThreshold', kind: 'text' },
      { key: 'outputConnection', labelKey: 'properties.outputConveyor', kind: 'text' }
    );
    defaultProps.color = '#334155';
    defaultProps.motorId = '';
    defaultProps.length = '400';
    defaultProps.width = '60';
    defaultProps.speed = '100';
    defaultProps.direction = 'RIGHT';
    defaultProps.endAction = 'DROP_OFF';
    defaultProps.minimumProductSpacing = '20';
    defaultProps.jamThresholdMs = '3000';
    defaultProps.outputConnection = '';
  }

  if (type === 'sensor') {
    schema.push(
      {
        key: 'variant',
        labelKey: 'properties.variant' as TranslationKey,
        kind: 'select',
        options: [
          { value: 'photoelectric', labelKey: 'sensor.variant.photoelectric' as TranslationKey },
          { value: 'inductive', labelKey: 'sensor.variant.inductive' as TranslationKey },
          { value: 'capacitive', labelKey: 'sensor.variant.capacitive' as TranslationKey },
          { value: 'light_curtain', labelKey: 'sensor.variant.light_curtain' as TranslationKey },
        ],
      },
      { key: 'range', labelKey: 'properties.sensingRange' as TranslationKey, kind: 'text' },
      {
        key: 'logicType',
        labelKey: 'properties.logicType' as TranslationKey,
        kind: 'select',
        options: [
          { value: 'NO', labelKey: 'logic.no' as TranslationKey },
          { value: 'NC', labelKey: 'logic.nc' as TranslationKey },
        ],
      },
      colorProperty
    );
    defaultProps.variant = 'photoelectric';
    defaultProps.range = '80';
    defaultProps.logicType = 'NO';
    defaultProps.color = '#0284c7';
  }

  if (type === 'jam-sensor') {
    schema.push(
      { key: 'conveyorId', labelKey: 'properties.conveyorId', kind: 'text' }
    );
    defaultProps.conveyorId = '';
  }

  if (type === 'tank') {
    schema.push(
      { key: 'capacity', labelKey: 'properties.capacity' as TranslationKey, kind: 'text' },
      { key: 'initialLevel', labelKey: 'properties.initialLevel' as TranslationKey, kind: 'text' },
      {
        key: 'inletCount',
        labelKey: 'properties.inletCount' as TranslationKey,
        kind: 'select',
        options: [
          { value: '1', labelKey: 'properties.inlet1' as TranslationKey },
          { value: '2', labelKey: 'properties.inlet2' as TranslationKey },
          { value: '3', labelKey: 'properties.inlet3' as TranslationKey },
          { value: '4', labelKey: 'properties.inlet4' as TranslationKey },
        ]
      },
      colorProperty
    );
    defaultProps.capacity = '1000';
    defaultProps.initialLevel = '0';
    defaultProps.inletCount = '1';
    defaultProps.color = 'var(--color-primary, #3b82f6)';
  }

  if (type === 'pump' || type === 'valve') {
    schema.push(
      { key: type === 'pump' ? 'nominalFlowRate' : 'maxFlowRate', labelKey: (type === 'pump' ? 'properties.nominalFlowRate' : 'properties.maxFlowRate') as TranslationKey, kind: 'text' },
      { key: 'sourceId', labelKey: 'properties.sourceId' as TranslationKey, kind: 'text' },
      { key: 'targetId', labelKey: 'properties.targetId' as TranslationKey, kind: 'text' }
    );
    if (type === 'pump') {
      schema.push(colorProperty);
      defaultProps.nominalFlowRate = '50';
      defaultProps.color = '#1e40af';
    }
    if (type === 'valve') {
      schema.push(
        colorProperty,
        {
          key: 'voltage',
          labelKey: 'properties.voltage' as TranslationKey,
          kind: 'select',
          options: [
            { value: '24V DC', labelKey: 'voltage.24vdc' as TranslationKey },
            { value: '230V AC', labelKey: 'voltage.230vac' as TranslationKey },
            { value: '12V DC', labelKey: 'voltage.12vdc' as TranslationKey },
          ],
        },
        {
          key: 'valveType',
          labelKey: 'properties.valveType' as TranslationKey,
          kind: 'select',
          options: [
            { value: 'NC', labelKey: 'valve.nc' as TranslationKey },
            { value: 'NO', labelKey: 'valve.no' as TranslationKey },
          ],
        }
      );
      defaultProps.maxFlowRate = '50';
      defaultProps.color = '#d97706';
      defaultProps.voltage = '24V DC';
      defaultProps.valveType = 'NC';
    }
    defaultProps.sourceId = '';
    defaultProps.targetId = '';
  }

  if (type === 'level-sensor') {
    schema.push(
      colorProperty,
      { key: 'tankId', labelKey: 'properties.tankId' as TranslationKey, kind: 'text' },
      { key: 'minLevel', labelKey: 'properties.minLevel' as TranslationKey, kind: 'text' },
      { key: 'maxLevel', labelKey: 'properties.maxLevel' as TranslationKey, kind: 'text' },
      { key: 'signalType', labelKey: 'properties.signalType' as TranslationKey, kind: 'select', options: [
        { value: '0-10V', labelKey: 'signal.0_10V' as TranslationKey },
        { value: '4-20mA', labelKey: 'signal.4_20mA' as TranslationKey }
      ]}
    );
    defaultProps.color = '#0284c7';
    defaultProps.tankId = '';
    defaultProps.minLevel = '0';
    defaultProps.maxLevel = '1000';
    defaultProps.signalType = '0-10V';
  }

  if (type === 'pid-controller') {
    schema.length = 0; // Remove address, PID usually uses source/target IDs or mapped MWs
    schema.push(
      { key: 'sourceId', labelKey: 'properties.pvSource' as TranslationKey, kind: 'text' },
      { key: 'targetId', labelKey: 'properties.cvTarget' as TranslationKey, kind: 'text' },
      { key: 'controlMode', labelKey: 'properties.controlMode' as TranslationKey, kind: 'select', options: [{ value: 'P', labelKey: 'P' as TranslationKey }, { value: 'PI', labelKey: 'PI' as TranslationKey }, { value: 'PID', labelKey: 'PID' as TranslationKey }] },
      { key: 'workMode', labelKey: 'properties.workMode' as TranslationKey, kind: 'select', options: [{ value: 'AUTO', labelKey: 'AUTO' as TranslationKey }, { value: 'MANUAL', labelKey: 'MANUAL' as TranslationKey }] },
      { key: 'sp', labelKey: 'properties.setpoint' as TranslationKey, kind: 'text' },
      { key: 'manualCv', labelKey: 'properties.manualCv' as TranslationKey, kind: 'text' },
      { key: 'kp', labelKey: 'properties.kp' as TranslationKey, kind: 'text' },
      { key: 'ki', labelKey: 'properties.ki' as TranslationKey, kind: 'text' },
      { key: 'kd', labelKey: 'properties.kd' as TranslationKey, kind: 'text' },
      { key: 'sampleTimeMs', labelKey: 'properties.sampleTimeMs' as TranslationKey, kind: 'text' },
      { key: 'minOutput', labelKey: 'properties.minOutput' as TranslationKey, kind: 'text' },
      { key: 'maxOutput', labelKey: 'properties.maxOutput' as TranslationKey, kind: 'text' }
    );
    Object.assign(defaultProps, {
      sourceId: '',
      targetId: '',
      controlMode: 'PI',
      workMode: 'AUTO',
      sp: '50',
      manualCv: '0',
      kp: '1.0',
      ki: '0.1',
      kd: '0',
      sampleTimeMs: '100',
      minOutput: '0',
      maxOutput: '100'
    });
  }

  if (type === 'modbus-server') {
    schema.push(
      { key: 'unitId', labelKey: 'properties.unitId' as TranslationKey, kind: 'text' },
      { key: 'isOnline', labelKey: 'properties.isOnline' as TranslationKey, kind: 'text' }
    );
    defaultProps.unitId = '1';
    defaultProps.isOnline = 'true';
    defaultProps.mappings = '[]';
  }

  if (type === 'modbus-client') {
    schema.push(
      { key: 'serverId', labelKey: 'properties.serverId' as TranslationKey, kind: 'text' },
      { key: 'unitId', labelKey: 'properties.unitId' as TranslationKey, kind: 'text' },
      { key: 'functionCode', labelKey: 'properties.functionCode' as TranslationKey, kind: 'text' },
      { key: 'startAddress', labelKey: 'properties.startAddress' as TranslationKey, kind: 'text' },
      { key: 'quantity', labelKey: 'properties.quantity' as TranslationKey, kind: 'text' },
      { key: 'writeValues', labelKey: 'properties.writeValues' as TranslationKey, kind: 'text' },
      { key: 'trigger', labelKey: 'properties.trigger' as TranslationKey, kind: 'text' },
      { key: 'delayMs', labelKey: 'properties.delayMs' as TranslationKey, kind: 'text' }
    );
    defaultProps.serverId = '';
    defaultProps.unitId = '1';
    defaultProps.functionCode = '3';
    defaultProps.startAddress = '0';
    defaultProps.quantity = '1';
    defaultProps.writeValues = '';
    defaultProps.trigger = 'false';
    defaultProps.delayMs = '20';
  }

  if (type === 'motor') {
    schema.push(
      {
        key: 'variant',
        labelKey: 'properties.variant' as TranslationKey,
        kind: 'select',
        options: [
          { value: 'motor', labelKey: 'device.motor.standard' as TranslationKey },
          { value: 'pump_motor', labelKey: 'device.motor.pumpMotor' as TranslationKey },
          { value: 'fan', labelKey: 'device.motor.fan' as TranslationKey },
          { value: 'mixer', labelKey: 'variant.mixer' as TranslationKey },
        ]
      },
      colorProperty
    );
    defaultProps.variant = 'motor';
    defaultProps.color = '#146E46';
    defaultProps.nominalFlowRate = '50';
    defaultProps.sourceId = '';
    defaultProps.targetId = '';
  }

  if (type === 'mixer') {
    schema.push(
      colorProperty,
      { key: 'tankId', labelKey: 'properties.tankId' as TranslationKey, kind: 'text' },
      { key: 'speed', labelKey: 'properties.speed' as TranslationKey, kind: 'text' },
      {
        key: 'bladeType',
        labelKey: 'properties.bladeType' as TranslationKey,
        kind: 'select',
        options: [
          { value: 'propeller', labelKey: 'blade.propeller' as TranslationKey },
          { value: 'turbine', labelKey: 'blade.turbine' as TranslationKey },
          { value: 'anchor', labelKey: 'blade.anchor' as TranslationKey },
        ],
      }
    );
    defaultProps.color = '#1e40af';
    defaultProps.tankId = '';
    defaultProps.speed = '100';
    defaultProps.bladeType = 'propeller';
  }

  if (type === 'fan') {
    schema.push(
      colorProperty,
      { key: 'speed', labelKey: 'properties.speed' as TranslationKey, kind: 'text' }
    );
    defaultProps.color = '#187AA0';
    defaultProps.speed = '100';
  }

  if (type === 'lamp') {
    schema.push(colorProperty);
    defaultProps.color = 'var(--color-warning, #eab308)';
  }

  if (type === 'button') {
    schema.push(
      colorProperty,
      { key: 'label', labelKey: 'properties.buttonLabel' as TranslationKey, kind: 'text' }
    );
    defaultProps.color = 'var(--color-success, #22aa22)';
    defaultProps.label = 'START';
  }

  if (type === 'emergency-stop') {
    schema.push(
      { key: 'label', labelKey: 'properties.buttonLabel' as TranslationKey, kind: 'text' }
    );
    defaultProps.label = 'EMERGENCY STOP';
  }

  return {
    type,
    category,
    availability: implementedTypes.includes(type) ? 'available' : 'planned',
    meta: {
      nameKey: `device.${type}.name` as TranslationKey,
      descriptionKey: `device.${type}.description` as TranslationKey,
      icon: type,
    },
    size: {
      width: type === 'conveyor' ? 400 : (type === 'sensor' ? 44 : (type === 'fan' ? 80 : (type === 'tank' ? 140 : (type === 'pump' ? 120 : (type === 'mixer' ? 50 : (type === 'valve' ? 100 : (type === 'level-sensor' ? 44 : GRID_UNIT))))))),
      height: type === 'conveyor' ? 85 : (type === 'sensor' ? 90 : (type === 'fan' ? 80 : (type === 'tank' ? 180 : (type === 'pump' ? 100 : (type === 'mixer' ? 140 : (type === 'valve' ? 100 : (type === 'level-sensor' ? 160 : GRID_UNIT))))))),
    },
    defaultProps: defaultProps as PropertyValues,
    propertySchema: schema,
    terminals: [],
  };
}

export function createDeviceRegistry(): DeviceRegistry {
  const registry = new DeviceRegistry();
  for (const [type, category] of PLANNED) registry.register(plannedDefinition(type, category));
  return registry;
}

/** Application-wide registry instance. */
export const deviceRegistry: DeviceRegistry = createDeviceRegistry();
